import type { ResumenMes } from '@/hooks/useResumenMes';
import type { MatrizAvance, ReporteSemanal } from '@adelante/shared';
import type ExcelJS from 'exceljs';
import type { ManoObraCalc } from './calcularManoObra';

// ExcelJS (~1MB) se carga bajo demanda al exportar, no en el bundle principal.
async function cargarExcel() {
  return (await import('exceljs')).default;
}

// Paleta corporativa.
const VERDE = 'FFADD010';
const NEGRO = 'FF0A0A0A';
const GRIS = 'FFF4F4F5';
const GRIS_TXT = 'FF52525B';
const ROJO = 'FFDC2626';

const FMT_CRC = '"₡"#,##0';
const FMT_PCT = '0.00"%"';
const FMT_NUM = '#,##0.0';

const bordeFino: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFE4E4E7' } },
  left: { style: 'thin', color: { argb: 'FFE4E4E7' } },
  bottom: { style: 'thin', color: { argb: 'FFE4E4E7' } },
  right: { style: 'thin', color: { argb: 'FFE4E4E7' } },
};

/** Rasteriza el logo SVG a PNG (data URL) vía canvas, para incrustarlo. */
async function logoPngDataUrl(): Promise<string | null> {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 745 326" width="745" height="326">
    <path fill-rule="evenodd" clip-rule="evenodd" fill="#88A024" d="M541.795 94.8125H331.851L189.631 325.073H402.984L541.795 94.8125ZM744.966 209.943H535.022L467.299 325.073H677.242L744.966 209.943Z"/>
    <path fill-rule="evenodd" clip-rule="evenodd" fill="#ADD010" d="M0 0H208.863L402.138 325.074H189.626L0 0ZM331.846 94.8132H541.79L677.237 325.074H467.907L331.846 94.8132Z"/>
  </svg>`;
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('logo'));
      img.src = url;
    });
    const w = 300;
    const h = Math.round((300 * 326) / 745);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function descargar(buf: ExcelJS.Buffer, nombre: string) {
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

/** Encabezado con logo + título + subtítulo en la hoja (filas 1-3). */
async function encabezado(
  wb: ExcelJS.Workbook,
  ws: ExcelJS.Worksheet,
  titulo: string,
  sub: string,
) {
  ws.mergeCells('A1:B3');
  const logo = await logoPngDataUrl();
  if (logo) {
    const id = wb.addImage({ base64: logo, extension: 'png' });
    ws.addImage(id, { tl: { col: 0.15, row: 0.3 }, ext: { width: 116, height: 51 } });
  }
  ws.mergeCells('C1:H2');
  const t = ws.getCell('C1');
  t.value = titulo;
  t.font = { bold: true, size: 16, color: { argb: NEGRO } };
  t.alignment = { vertical: 'middle' };
  ws.mergeCells('C3:H3');
  const s = ws.getCell('C3');
  s.value = sub;
  s.font = { size: 10, color: { argb: GRIS_TXT } };
}

/** Fila de título de sección (barra negra de ancho completo). */
function seccion(ws: ExcelJS.Worksheet, fila: number, texto: string) {
  ws.mergeCells(fila, 1, fila, 6);
  const c = ws.getCell(fila, 1);
  c.value = texto;
  c.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
  c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NEGRO } };
  c.alignment = { vertical: 'middle', indent: 1 };
  ws.getRow(fila).height = 20;
}

type Kpi = { label: string; value: string | number; fmt?: string; rojo?: boolean };

/** Coloca KPIs en una grilla de 3 columnas (label + valor). */
function kpis(ws: ExcelJS.Worksheet, filaIni: number, items: Kpi[]): number {
  let fila = filaIni;
  items.forEach((k, i) => {
    const col = i % 3;
    if (i > 0 && col === 0) fila++;
    const lc = ws.getCell(fila, 1 + col * 2);
    const vc = ws.getCell(fila, 2 + col * 2);
    lc.value = k.label;
    lc.font = { bold: true, size: 9, color: { argb: GRIS_TXT } };
    lc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GRIS } };
    lc.alignment = { vertical: 'middle', wrapText: true, indent: 1 };
    lc.border = bordeFino;
    vc.value = k.value;
    vc.font = { bold: true, size: 11, color: { argb: k.rojo ? ROJO : NEGRO } };
    vc.alignment = { horizontal: 'right', vertical: 'middle' };
    vc.border = bordeFino;
    if (k.fmt) vc.numFmt = k.fmt;
    ws.getRow(fila).height = 22;
  });
  return fila + 1;
}

/** Línea de nota (texto chico, gris) combinada a lo ancho de la hoja. */
function notaCalculo(ws: ExcelJS.Worksheet, fila: number, texto: string) {
  ws.mergeCells(fila, 1, fila, 6);
  const c = ws.getCell(fila, 1);
  c.value = texto;
  c.font = { italic: true, size: 8, color: { argb: GRIS_TXT } };
  c.alignment = { wrapText: true, vertical: 'top' };
  ws.getRow(fila).height = 24;
}

/** Estiliza la fila de encabezado de una tabla (verde, negrita). */
function encabezadoTabla(ws: ExcelJS.Worksheet, fila: number, ncols: number) {
  const row = ws.getRow(fila);
  row.height = 22;
  for (let c = 1; c <= ncols; c++) {
    const cell = row.getCell(c);
    cell.font = { bold: true, size: 10, color: { argb: NEGRO } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: VERDE } };
    cell.alignment = { vertical: 'middle', wrapText: true };
    cell.border = bordeFino;
  }
}

/** Título simple (sin logo) para hojas secundarias. */
function tituloHoja(ws: ExcelJS.Worksheet, texto: string, sub?: string) {
  ws.mergeCells('A1:F1');
  const t = ws.getCell('A1');
  t.value = texto;
  t.font = { bold: true, size: 14, color: { argb: NEGRO } };
  if (sub) {
    ws.mergeCells('A2:F2');
    const s = ws.getCell('A2');
    s.value = sub;
    s.font = { size: 9, color: { argb: GRIS_TXT } };
  }
}

function fmtPeriodo(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
}

// --- Hoja: Resumen del Mes ---
function agregarHojaResumenMes(wb: ExcelJS.Workbook, resumen: ResumenMes, semanaSelId: number) {
  const ws = wb.addWorksheet('Resumen del Mes', { views: [{ showGridLines: false }] });
  const cab = [
    'Semana',
    'Período',
    'Días Ef.',
    '% Crono Esp',
    '% Crono Real',
    '% Costo Esp',
    '% Costo Real',
  ];
  ws.addRow(cab);
  encabezadoTabla(ws, 1, cab.length);
  for (const f of resumen.filas) {
    const row = ws.addRow([
      `S${f.semana.numero_semana}`,
      `${fmtPeriodo(f.semana.fecha_inicio)} → ${fmtPeriodo(f.semana.fecha_fin)}`,
      f.semana.dias_efectivos,
      f.cronoEsp,
      f.baseSemanal ? f.cronoReal : '—',
      f.costoEsp,
      f.baseSemanal ? f.costoReal : '—',
    ]);
    row.eachCell((c) => {
      c.border = bordeFino;
      c.font = { size: 10 };
    });
    for (const c of [4, 5, 6, 7]) {
      if (typeof row.getCell(c).value === 'number') row.getCell(c).numFmt = FMT_PCT;
    }
    if (Number(f.semana.id) === semanaSelId) {
      row.eachCell((c) => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7FEE7' } };
      });
    }
  }
  const totRow = ws.addRow([
    'Total a la fecha',
    '',
    '',
    resumen.tot.cronoEsp,
    resumen.tot.cronoReal,
    resumen.tot.costoEsp,
    resumen.tot.costoReal,
  ]);
  totRow.font = { bold: true };
  for (const c of [4, 5, 6, 7]) totRow.getCell(c).numFmt = FMT_PCT;
  const difRow = ws.addRow([
    'Diferencia (real − esp.)',
    '',
    '',
    '',
    resumen.difCrono,
    '',
    resumen.difCosto,
  ]);
  difRow.font = { bold: true };
  difRow.getCell(5).numFmt = FMT_PCT;
  difRow.getCell(7).numFmt = FMT_PCT;
  if (resumen.difCrono < 0) difRow.getCell(5).font = { bold: true, color: { argb: ROJO } };
  if (resumen.difCosto < 0) difRow.getCell(7).font = { bold: true, color: { argb: ROJO } };
  ws.columns.forEach((c, i) => {
    c.width = i === 0 ? 12 : i === 1 ? 18 : 14;
  });
}

// --- Hoja: Financiero (Indirecto / Venta / Utilidad) ---
function agregarHojaFinanciero(
  wb: ExcelJS.Workbook,
  reporte: ReporteSemanal,
  resumen?: ResumenMes,
) {
  const { semana, totales, obras } = reporte;
  const ws = wb.addWorksheet('Financiero', { views: [{ showGridLines: false }] });
  ws.columns = [
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
  ];
  tituloHoja(
    ws,
    `Financiero — Semana ${semana.numero_semana}/${semana.anio}`,
    `${semana.fecha_inicio.slice(0, 10)} a ${semana.fecha_fin.slice(0, 10)} · ${semana.dias_efectivos} días efectivos · ${semana.estado}`,
  );

  let f = 4;
  seccion(ws, f++, 'PRODUCCIÓN SEMANA (₡)');
  f = kpis(ws, f, [
    { label: 'Costo Directo', value: Math.round(totales.monto_semana), fmt: FMT_CRC },
    { label: 'Indirecto', value: Math.round(totales.indirecto_semana), fmt: FMT_CRC },
    { label: 'Venta', value: Math.round(totales.venta_semana), fmt: FMT_CRC },
    {
      label: 'Utilidad',
      value: Math.round(totales.utilidad_semana),
      fmt: FMT_CRC,
      rojo: totales.utilidad_semana < 0,
    },
  ]);
  f++;
  seccion(ws, f++, 'PRODUCCIÓN ACUMULADA (₡)');
  f = kpis(ws, f, [
    { label: 'Costo Directo', value: Math.round(totales.monto_acumulado), fmt: FMT_CRC },
    { label: 'Indirecto', value: Math.round(totales.indirecto_acumulado), fmt: FMT_CRC },
    { label: 'Venta', value: Math.round(totales.venta_acumulada), fmt: FMT_CRC },
    {
      label: 'Utilidad',
      value: Math.round(totales.utilidad_acumulada),
      fmt: FMT_CRC,
      rojo: totales.utilidad_acumulada < 0,
    },
  ]);

  // Resumen del mes (producción financiera por semana).
  if (resumen?.filas.length) {
    f++;
    seccion(ws, f++, 'RESUMEN DEL MES — PRODUCCIÓN POR SEMANA (₡)');
    const cabMes = ['Semana', 'Período', 'Directo', 'Indirecto', 'Venta', 'Utilidad'];
    const headFila = f;
    const hr = ws.getRow(headFila);
    cabMes.forEach((t, i) => {
      hr.getCell(i + 1).value = t;
    });
    encabezadoTabla(ws, headFila, cabMes.length);
    f++;
    for (const fl of resumen.filas) {
      const row = ws.getRow(f++);
      row.getCell(1).value = `S${fl.semana.numero_semana}`;
      row.getCell(2).value =
        `${fmtPeriodo(fl.semana.fecha_inicio)} → ${fmtPeriodo(fl.semana.fecha_fin)}`;
      row.getCell(3).value = fl.baseSemanal ? Math.round(fl.directo) : '—';
      row.getCell(4).value = fl.baseSemanal ? Math.round(fl.indirecto) : '—';
      row.getCell(5).value = fl.baseSemanal ? Math.round(fl.venta) : '—';
      row.getCell(6).value = fl.baseSemanal ? Math.round(fl.utilidad) : '—';
      row.eachCell((c) => {
        c.border = bordeFino;
        c.font = { size: 10 };
      });
      for (const c of [3, 4, 5, 6])
        if (typeof row.getCell(c).value === 'number') row.getCell(c).numFmt = FMT_CRC;
    }
    const totMes = ws.getRow(f++);
    totMes.getCell(1).value = 'Total a la fecha';
    totMes.getCell(3).value = Math.round(resumen.totFin.directo);
    totMes.getCell(4).value = Math.round(resumen.totFin.indirecto);
    totMes.getCell(5).value = Math.round(resumen.totFin.venta);
    totMes.getCell(6).value = Math.round(resumen.totFin.utilidad);
    totMes.font = { bold: true };
    for (const c of [3, 4, 5, 6]) totMes.getCell(c).numFmt = FMT_CRC;
  }

  // Detalle por obra (semana + acumulado).
  const wd = wb.addWorksheet('Financiero Detalle', { views: [{ state: 'frozen', ySplit: 1 }] });
  const cab = [
    'Obra',
    'Proyecto',
    'Tipo',
    '% Costo Sem',
    'm² Sem',
    'Directo Sem ₡',
    'Indirecto Sem ₡',
    'Venta Sem ₡',
    'Utilidad Sem ₡',
    '% Costo Acum',
    'm² Acum',
    'Directo Acum ₡',
    'Indirecto Acum ₡',
    'Venta Acum ₡',
    'Utilidad Acum ₡',
  ];
  wd.addRow(cab);
  encabezadoTabla(wd, 1, cab.length);
  for (const o of obras) {
    const row = wd.addRow([
      o.codigo,
      o.proyecto_codigo,
      o.tipo_casa ?? '',
      o.costo_semana,
      o.m2_semana,
      Math.round(o.monto_semana),
      Math.round(o.indirecto_semana),
      Math.round(o.venta_semana),
      Math.round(o.utilidad_semana),
      o.costo_acumulado,
      o.m2_acumulado,
      Math.round(o.monto_acumulado),
      Math.round(o.indirecto_acumulado),
      Math.round(o.venta_acumulada),
      Math.round(o.utilidad_acumulada),
    ]);
    row.eachCell((c) => {
      c.border = bordeFino;
      c.font = { size: 9 };
    });
    row.getCell(4).numFmt = FMT_PCT;
    row.getCell(5).numFmt = FMT_NUM;
    row.getCell(10).numFmt = FMT_PCT;
    row.getCell(11).numFmt = FMT_NUM;
    for (const c of [6, 7, 8, 9, 12, 13, 14, 15]) row.getCell(c).numFmt = FMT_CRC;
  }
  wd.columns.forEach((c, i) => {
    c.width = i === 0 ? 12 : i === 1 ? 11 : i === 2 ? 10 : 14;
  });
}

// --- Hojas: Mano de Obra (Indicadores + Distribución) ---
function agregarHojasMO(
  wb: ExcelJS.Workbook,
  semana: ReporteSemanal['semana'],
  calc: ManoObraCalc,
) {
  const ws = wb.addWorksheet('Indicadores M.O.', { views: [{ showGridLines: false }] });
  ws.columns = [
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
  ];
  tituloHoja(
    ws,
    `Mano de Obra — Semana ${semana.numero_semana}/${semana.anio}`,
    `${semana.fecha_inicio.slice(0, 10)} a ${semana.fecha_fin.slice(0, 10)}`,
  );
  let f = 4;
  seccion(ws, f++, 'PRODUCCIÓN');
  f = kpis(ws, f, [
    { label: 'm² Construidos Semana', value: calc.m2Sem, fmt: FMT_NUM },
    { label: 'Costo teórico/m²', value: Math.round(calc.teorico), fmt: FMT_CRC },
    { label: 'Eficiencia', value: calc.eficiencia, fmt: FMT_PCT, rojo: calc.eficiencia < 100 },
  ]);
  f++;
  seccion(ws, f++, 'HORAS HOMBRE');
  f = kpis(ws, f, [
    { label: 'HH Directas Semana', value: calc.hhDirectas, fmt: FMT_NUM },
    { label: 'HH Equiv. Subcontrato', value: calc.hhEquivSubc, fmt: FMT_NUM },
    { label: 'Costo Prom HH C/Cargas', value: Math.round(calc.costoPromHH), fmt: FMT_CRC },
    { label: 'HH/m² Sin Subcontrato', value: calc.hhM2Sin, fmt: '0.00' },
    { label: 'HH/m² Con Subcontrato', value: calc.hhM2Con, fmt: '0.00' },
    { label: 'Costo MO por m²', value: Math.round(calc.costoMOm2), fmt: FMT_CRC },
  ]);
  f++;
  seccion(ws, f++, 'COSTOS MANO DE OBRA (₡)');
  f = kpis(ws, f, [
    { label: 'MO Directa Semana', value: Math.round(calc.moDirecta), fmt: FMT_CRC },
    { label: 'MO Subcontrato', value: Math.round(calc.moSubcontrato), fmt: FMT_CRC },
    { label: 'MO Total Gastada', value: Math.round(calc.moTotalGastada), fmt: FMT_CRC },
    { label: 'MO Presupuestada', value: Math.round(calc.moPresupuestada), fmt: FMT_CRC },
    {
      label: 'Sobrecosto MO',
      value: Math.round(calc.sobrecosto),
      fmt: FMT_CRC,
      rojo: calc.sobrecosto > 0,
    },
  ]);

  const wd = wb.addWorksheet('Distribución M.O.', { views: [{ state: 'frozen', ySplit: 1 }] });
  const cab = [
    'Obra',
    'Horas',
    '% Horas',
    'Nómina Asignada ₡',
    'Subcontratos ₡',
    'M.O. Total ₡',
    'm² Sem',
    'Costo M.O./m² ₡',
  ];
  wd.addRow(cab);
  encabezadoTabla(wd, 1, cab.length);
  for (const o of calc.obras) {
    const row = wd.addRow([
      o.codigo,
      o.hrs,
      o.pctHoras,
      Math.round(o.nominaAsig),
      Math.round(o.sc),
      Math.round(o.moTotal),
      o.m2,
      Math.round(o.costoM2),
    ]);
    row.eachCell((c) => {
      c.border = bordeFino;
      c.font = { size: 9 };
    });
    row.getCell(2).numFmt = FMT_NUM;
    row.getCell(3).numFmt = FMT_PCT;
    row.getCell(7).numFmt = FMT_NUM;
    for (const c of [4, 5, 6, 8]) row.getCell(c).numFmt = FMT_CRC;
  }
  wd.columns.forEach((c, i) => {
    c.width = i === 0 ? 12 : 16;
  });
}

// --- Hoja: Matriz de Avance ---
function agregarHojaMatriz(wb: ExcelJS.Workbook, matriz: MatrizAvance) {
  const ws = wb.addWorksheet('Matriz de Avance', {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }],
  });
  const cab = [
    'Obra',
    'Tipo',
    'Sprint',
    ...matriz.partidas.map((p) => p.nombre),
    'Crono',
    'General',
  ];
  ws.addRow(cab);
  encabezadoTabla(ws, 1, cab.length);
  for (const o of matriz.obras) {
    const celdas = matriz.partidas.map((p) => {
      const v = o.celdas[p.id];
      return v == null ? '—' : Math.round(v);
    });
    const row = ws.addRow([
      o.codigo,
      o.tipo_casa,
      o.sprint_actual,
      ...celdas,
      Math.round(o.avance_crono),
      Math.round(o.avance_general),
    ]);
    row.eachCell((c) => {
      c.border = bordeFino;
      c.font = { size: 9 };
      c.alignment = { horizontal: 'center' };
    });
    row.getCell(1).alignment = { horizontal: 'left' };
  }
  ws.columns.forEach((c, i) => {
    c.width = i === 0 ? 12 : i === 1 ? 9 : i < 3 ? 7 : 7;
  });
  // Encabezado: alto + texto vertical para que quepan los nombres de partidas.
  const head = ws.getRow(1);
  head.height = 90;
  for (let c = 4; c <= cab.length; c++) {
    head.getCell(c).alignment = { textRotation: 90, vertical: 'bottom', horizontal: 'center' };
  }
}

// ===========================================================================
// Export unificado: un Excel con todas las hojas.
// ===========================================================================

export async function exportarReporteXlsx(
  reporte: ReporteSemanal,
  extra: { resumenMes?: ResumenMes; moCalc?: ManoObraCalc | null; matriz?: MatrizAvance | null },
) {
  const { semana, totales, obras } = reporte;
  const Excel = await cargarExcel();
  const wb = new Excel.Workbook();
  wb.creator = 'Adelante · ObrasControl';

  // --- Hoja 1: Resumen Semanal ---
  const ws = wb.addWorksheet('Resumen Semanal', {
    views: [{ showGridLines: false }],
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1 },
  });
  ws.columns = [
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
    { width: 24 },
    { width: 16 },
  ];
  await encabezado(
    wb,
    ws,
    `Reporte de Producción — Semana ${semana.numero_semana}/${semana.anio}`,
    `${semana.fecha_inicio.slice(0, 10)} a ${semana.fecha_fin.slice(0, 10)} · ${semana.dias_efectivos} días efectivos · ${semana.estado}`,
  );

  let f = 5;
  seccion(
    ws,
    f++,
    totales.parcial
      ? 'CRONOGRAMA Y COSTO — SEMANA · PARCIAL (SEMANA ABIERTA)'
      : 'CRONOGRAMA Y COSTO — SEMANA',
  );
  f = kpis(ws, f, [
    { label: '% Crono Real', value: totales.crono_real_prom, fmt: FMT_PCT, rojo: true },
    { label: '% Crono Esperado', value: totales.crono_esperado_prom, fmt: FMT_PCT },
    { label: 'Con Trabajo', value: totales.con_trabajo },
    { label: '% Costo Real', value: totales.costo_real_prom, fmt: FMT_PCT, rojo: true },
    { label: '% Costo Esperado', value: totales.costo_esperado_prom, fmt: FMT_PCT },
    { label: 'Sin Trabajo', value: totales.sin_trabajo },
    { label: 'Cumplimiento Crono', value: totales.cumplimiento_crono ?? '—', fmt: FMT_PCT },
    { label: 'Cumplimiento Costo', value: totales.cumplimiento_costo ?? '—', fmt: FMT_PCT },
    { label: 'Eficiencia Meta Crono', value: totales.eficiencia_crono, fmt: FMT_PCT },
    { label: '% Costo Real Ponderado ₡', value: totales.costo_real_pond, fmt: FMT_PCT, rojo: true },
    { label: '% Costo Esp. Ponderado ₡', value: totales.costo_esperado_pond, fmt: FMT_PCT },
    {
      label: 'Cumplimiento Costo Pond.',
      value: totales.cumplimiento_costo_pond ?? '—',
      fmt: FMT_PCT,
    },
  ]);
  notaCalculo(
    ws,
    f++,
    'Promedios sobre las casas Con Trabajo. % Crono Esperado = meta del tipo (100 / sprints) × eficiencia × días/5. Ponderado ₡ = Σ avance ₡ / Σ presupuesto.',
  );
  f++;
  seccion(ws, f++, 'ÁREAS (m²)');
  f = kpis(ws, f, [
    { label: 'Área Total Construcción', value: totales.area_total, fmt: FMT_NUM },
    { label: 'Área Construida', value: totales.area_construida, fmt: FMT_NUM },
    { label: 'Área Por Construir', value: totales.area_por_construir, fmt: FMT_NUM },
    { label: 'm² Construidos Semana', value: totales.m2_semana, fmt: FMT_NUM },
    { label: 'm² Esperados Semana', value: totales.m2_esperado, fmt: FMT_NUM },
  ]);
  f++;
  seccion(ws, f++, 'ECONÓMICO (₡)');
  f = kpis(ws, f, [
    { label: 'Avance Económico Semana', value: Math.round(totales.monto_semana), fmt: FMT_CRC },
    { label: 'Avance Económico Acum.', value: Math.round(totales.monto_acumulado), fmt: FMT_CRC },
    { label: 'Presupuesto Total', value: Math.round(totales.presupuesto_total), fmt: FMT_CRC },
    {
      label: 'Faltante por Invertir',
      value: Math.round(totales.faltante),
      fmt: FMT_CRC,
      rojo: true,
    },
  ]);
  f++;
  seccion(ws, f++, 'ESTADO DE OBRAS — CONSTRUCCIÓN');
  f = kpis(ws, f, [
    { label: 'Construcción', value: totales.construccion },
    { label: 'En Ejecución', value: totales.trabajando },
    { label: 'En Espera', value: totales.en_espera_sprint },
    { label: 'Congeladas', value: totales.en_espera },
    { label: '1N Activas', value: totales.tipo_1n },
    { label: '2N Activas', value: totales.tipo_2n },
  ]);

  seccion(ws, f++, 'FUERA DE CONSTRUCCIÓN');
  f = kpis(ws, f, [
    { label: 'Inactivas', value: totales.inactivas },
    { label: 'Terminadas', value: totales.terminadas },
    { label: 'Por Iniciar', value: totales.pendientes },
  ]);

  seccion(ws, f++, 'ESTADO DE OBRAS — VENTA');
  f = kpis(ws, f, [
    { label: 'Formalizadas', value: totales.formalizadas },
    { label: 'Entregadas', value: totales.entregadas },
    { label: 'Reservadas', value: totales.reservadas },
    { label: 'Disponibles', value: totales.disponibles },
    { label: 'Sin Formalizar (Reserv.+Disp.)', value: totales.sin_formalizar },
  ]);

  // --- Hoja: Resumen del Mes ---
  if (extra.resumenMes) agregarHojaResumenMes(wb, extra.resumenMes, Number(semana.id));

  // --- Hoja: Detalle Obras ---
  const wd = wb.addWorksheet('Detalle Obras', { views: [{ state: 'frozen', ySplit: 1 }] });
  const cab = [
    'Obra',
    'Proyecto',
    'Tipo',
    'Sprint',
    'm²',
    '% Crono Esp',
    '% Crono Sem',
    '% Costo Esp',
    '% Costo Sem',
    'm² Sem Esp',
    'm² Sem',
    'Avance Econ ₡',
    '% Crono Acum',
    '% Costo Acum',
    'm² Acum',
    'Subs',
    'Estado',
    'Venta',
  ];
  wd.addRow(cab);
  encabezadoTabla(wd, 1, cab.length);
  for (const o of obras) {
    const row = wd.addRow([
      o.codigo,
      o.proyecto_codigo,
      o.tipo_casa ?? '',
      o.sprint_actual ?? '',
      o.m2,
      o.crono_esperado,
      o.crono_semana,
      o.costo_esperado,
      o.costo_semana,
      o.m2_esperado,
      o.m2_semana,
      Math.round(o.monto_semana),
      o.crono_acumulado,
      o.costo_acumulado,
      o.m2_acumulado,
      `${o.subs_completadas}/${o.subs_total}`,
      o.estado,
      o.estado_venta ?? '',
    ]);
    row.eachCell((c) => {
      c.border = bordeFino;
      c.font = { size: 9 };
    });
    for (const c of [5, 6, 7, 8, 9, 10, 11, 13, 14, 15]) row.getCell(c).numFmt = FMT_NUM;
    row.getCell(12).numFmt = FMT_CRC;
  }
  wd.columns.forEach((c, i) => {
    c.width = i === 0 ? 12 : i === 1 ? 12 : i === 16 ? 14 : 11;
  });

  // --- Hojas: Financiero (resumen + detalle por obra) ---
  agregarHojaFinanciero(wb, reporte, extra.resumenMes);

  // --- Hojas: Mano de Obra (Indicadores + Distribución) ---
  if (extra.moCalc) agregarHojasMO(wb, semana, extra.moCalc);

  // --- Hoja: Matriz de Avance ---
  if (extra.matriz) agregarHojaMatriz(wb, extra.matriz);

  descargar(await wb.xlsx.writeBuffer(), `Reporte_S${semana.numero_semana}_${semana.anio}.xlsx`);
}
